import { useState } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Popover, Tooltip, IconButton } from '@mui/material';
import { Info } from 'lucide-react';

interface IHelpCalloutProps {
  label: string | React.ReactNode | undefined;
  labelId: string;
  fieldId: string | undefined;
  helpText: string | React.ReactNode;
  style?: React.CSSProperties;
  tabIndex?: number;
  maxWidth?: number;
}

export const HelpCallout = (props: IHelpCalloutProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const descriptionId = useId();
  const iconButtonId = useId();
  const { t } = useTranslation();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        style={props.style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          maxWidth: '300px',
          mb: '-3px',
        }}
      >
        <label id={props.labelId} htmlFor={props.fieldId}>
          {props.label}
        </label>
        <Tooltip title={t('tooltips.info')}>
          <IconButton id={iconButtonId} onClick={handleClick} aria-label={t('tooltips.info')} tabIndex={props.tabIndex}>
            <Info size={16} />
          </IconButton>
        </Tooltip>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: { sx: { p: 2, maxWidth: props.maxWidth || 200, minWidth: 'auto' } },
        }}
        aria-describedby={descriptionId}
        role="alertdialog"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', whiteSpace: 'normal' }}>
          {typeof props.helpText === 'string' ? (
            <span id={descriptionId}>{props.helpText}</span>
          ) : (
            <Box id={descriptionId}>{props.helpText}</Box>
          )}
          <Button variant="contained" onClick={handleClose} sx={{ alignSelf: 'flex-start', width: 'auto' }}>
            {t('labels.closeButton')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};
